import React, { useCallback, useState } from 'react';
import cloneDeep from 'lodash/cloneDeep';

import api from '../api';
import { useGet, useGetOne, usePost, usePut, useRemove, useSoftDelete } from '../../apiHooks';
import { TravelFile as OldTravelFile, TravelFileService as OldTravelFileService } from '../oldModelTypes';
import { checkIsTourOrHotelService } from '../../../routes/Reservations/TravelFile/travelFileHelpers';

import Context, { type ContextTravelFile } from './context';

interface Props {
    children: React.ReactNode;
}

function TravelFileProvider({ children }: Props) {
    const [data, setData] = useState<ContextTravelFile['data']>([]);
    const [selected, setSelected] = useState<OldTravelFile | null>(null);
    const [error, setError] = useState<ContextTravelFile['error']>({});
    const [selectedPassengersIds, setSelectedPassengersIds] =
        useState<ContextTravelFile['selectedPassengersIds']>(null);
    const [priceExcludedPassengerIdsByTf, setPriceExcludedPassengerIdsByTf] =
        useState<ContextTravelFile['priceExcludedPassengerIdsByTf']>({});
    const [tourPassengersData, setTourPassengersData] = useState<ContextTravelFile['tourPassengersData']>([]);

    const resetContext = useCallback(() => {
        setData([]);
        setSelected(null);
        setError({});
    }, []);

    const handleGetData = useCallback((res: ContextTravelFile['data']) => {
        setData(res);
        return res;
    }, []);
    const get = useGet(api, data, handleGetData, setError);
    // const saveUseGet = useGet(api, data, setData, setError);
    // const get = (params: any, options: ModelOptions, updateState?: boolean) => {
    //     if (!data.length) {
    //         return saveUseGet(params, options, updateState);
    //     } else {
    //         return Promise.resolve(data);
    //     }
    // };
    const getOne = useGetOne(api, setSelected, setError);

    const getTravelFile = useCallback<ContextTravelFile['getTravelFile']>(
        async (query, updateState = true) => {
            try {
                const travelFile = await api.getTravelFile(query);
                if (updateState) {
                    setSelected(travelFile);
                }
                return travelFile;
            } catch (err) {
                throw err;
            }
        },
        []
    );

    const getTableTravelFiles = useCallback<ContextTravelFile['getTableTravelFiles']>((body) => {
        return api
            .getTableTravelFiles(body)
            .then((travelFiles: OldTravelFile[]) => {
                setData(travelFiles);
                return travelFiles;
            })
            .catch((err) => {
                throw err;
            });
    }, []);

    const queryFn = useCallback<ContextTravelFile['query']>(async (body) => {
        try {
            const travelFiles = await api.query(body);
            setData(travelFiles);
            return travelFiles;
        } catch (err) {
            throw err;
        }
    }, []);

    const getTravelFilesByTicketNumber = useCallback<ContextTravelFile['getTravelFilesByTicketNumber']>(
        (query) => {
            return api
                .getTravelFilesByTicketNumber(query)
                .then((travelFiles: OldTravelFile[]) => {
                    setData(travelFiles);
                    return travelFiles;
                })
                .catch((err) => {
                    throw err;
                });
        },
        []
    );

    const getTravelFiles = useCallback<ContextTravelFile['getTravelFiles']>((query) => {
        return api
            .getTravelFiles(query)
            .then((travelFiles: OldTravelFile[]) => {
                setData(travelFiles);
                return travelFiles;
            })
            .catch((err) => {
                throw err;
            });
    }, []);

    const getTravelFilesByIds = useCallback<ContextTravelFile['getTravelFilesByIds']>((ids, options) => {
        return api.getTravelFilesByIds(ids, options).catch((err) => {
            throw err;
        });
    }, []);

    const getLedgerNumbering = useCallback<ContextTravelFile['getLedgerNumbering']>(
        () => api.getLedgerNumbering(),
        []
    );

    const sendTravelFileToCrm = useCallback<ContextTravelFile['sendTravelFileToCrm']>(
        (travelFileId) => api.sendTravelFileToCrm(travelFileId),
        []
    );

    const markTravelFilesLedgerExported = useCallback<ContextTravelFile['markTravelFilesLedgerExported']>(
        (ids) => api.markTravelFilesLedgerExported(ids),
        []
    );

    const getTravelFilesByTourDetails = useCallback<ContextTravelFile['getTravelFilesByTourDetails']>(
        (query) => {
            return api
                .getTravelFilesByTourDetails(query)
                .then((travelFiles: OldTravelFile[]) => {
                    setData(travelFiles); // !!!!!!!!!!!
                    return travelFiles;
                })
                .catch((err) => {
                    throw err;
                });
        },
        []
    );

    const getTravelFilesByInventory = useCallback<ContextTravelFile['getTravelFilesByInventory']>((query) => {
        return api
            .getTravelFilesByInventory(query)
            .then((travelFiles: OldTravelFile[]) => {
                setData(travelFiles); // !!!!!!!!!!!
                return travelFiles;
            })
            .catch((err) => {
                throw err;
            });
    }, []);

    const getTravelFilesByPassengerDetails = useCallback<
        ContextTravelFile['getTravelFilesByPassengerDetails']
    >((query) => {
        return api
            .getTravelFilesByPassengerDetails(query)
            .then((travelFiles: OldTravelFile[]) => {
                setData(travelFiles);
                return travelFiles;
            })
            .catch((err) => {
                throw err;
            });
    }, []);

    const getSalesAndProfit = useCallback<ContextTravelFile['getSalesAndProfit']>((query) => {
        return api.getSalesAndProfit(query);
    }, []);

    const getSalesAndProfitSummary = useCallback<ContextTravelFile['getSalesAndProfitSummary']>((query) => {
        return api.getSalesAndProfitSummary(query);
    }, []);

    const getLeftToPayReport = useCallback<ContextTravelFile['getLeftToPayReport']>(
        (query: any) => {
            return api.getLeftToPayReport(query);
        },
        []
    );

    const getCancelledPassengersReport = useCallback<ContextTravelFile['getCancelledPassengersReport']>(
        (query) => {
            return api.getCancelledPassengersReport(query);
        },
        []
    );
    const getReservationsReport = useCallback<ContextTravelFile['getReservationsReport']>((query) => {
        return api.getReservationsReport(query);
    }, []);

    const handleSetData = useCallback((_item: never, newData: ContextTravelFile['data']) => {
        setData(newData);
    }, []);
    const post = usePost(api, data, handleSetData, setError);

    const handlePutCallback = useCallback(
        (response: ContextTravelFile['selected'], newData: ContextTravelFile['data']) => {
            setData(newData);
            setSelected(response);
        },
        []
    );
    const put = usePut(api, data, handlePutCallback, setError);

    const handleDeleteCallback = useCallback((id: string | number, newData: ContextTravelFile['data']) => {
        setData(newData);
        setSelected(null);
        return id;
    }, []);
    const remove = useRemove(api, data, handleDeleteCallback, setError);

    const softDelete = useSoftDelete(api, data, handleDeleteCallback, setError);

    const getTourServicePassengersByTravelFile = useCallback<
        ContextTravelFile['getTourServicePassengersByTravelFile']
    >(async (query) => {
        try {
            const passengers = await api.getTourServicePassengersByTravelFile(query);
            setTourPassengersData(passengers);
            return passengers;
        } catch (err) {
            throw err;
        }
    }, []);

    const getTourPassengersByTourId = useCallback<ContextTravelFile['getTourPassengersByTourId']>(
        async (query) => {
            try {
                const passengers = await api.getTourPassengersByTourId(query);
                setTourPassengersData(passengers);
                return passengers;
            } catch (err) {
                throw err;
            }
        },
        []
    );

    const createTravelFileByService = useCallback<ContextTravelFile['createTravelFileByService']>(
        async (body) => {
            try {
                const travelFile = await api.createTravelFileByService(body);
                return travelFile;
            } catch (err) {
                throw err;
            }
        },
        []
    );

    const createTravelFileNoService = useCallback<ContextTravelFile['createTravelFileNoService']>(
        async (body) => {
            try {
                const travelFile = await api.createTravelFileNoService(body);
                return travelFile;
            } catch (err) {
                throw err;
            }
        },
        []
    );

    const updateTravelFileService = useCallback<ContextTravelFile['updateTravelFileService']>(
        async (body) => {
            try {
                const updatedService: OldTravelFileService = await api.updateTravelFileService(body);
                const updatedServiceIndex = (selected as OldTravelFile).services.findIndex(
                    (ser) => ser._id === updatedService._id
                );
                const updateTravelFile = cloneDeep(selected as OldTravelFile);
                // Merge updates into existing service to preserve joined/computed fields
                updateTravelFile.services[updatedServiceIndex] = {
                    ...updateTravelFile.services[updatedServiceIndex],
                    ...updatedService
                };
                setSelected(updateTravelFile);
                return updateTravelFile.services[updatedServiceIndex];
            } catch (err) {
                setError(err as object);
                throw err;
            }
        },
        [selected]
    );

    const amendTravelFileService = useCallback<ContextTravelFile['amendTravelFileService']>(
        async (body) => {
            try {
                const { amendWarnings, amendNotices, ...updatedService } = (await api.amendTravelFileService(
                    body
                )) as OldTravelFileService & { amendWarnings?: string[]; amendNotices?: string[] };
                const updatedServiceIndex = (selected as OldTravelFile).services.findIndex(
                    (ser) => ser._id === updatedService._id
                );
                const updateTravelFile = cloneDeep(selected as OldTravelFile);
                updateTravelFile.services[updatedServiceIndex] = updatedService as OldTravelFileService;
                setSelected(updateTravelFile);
                return { warnings: amendWarnings || [], notices: amendNotices || [] };
            } catch (err) {
                setError(err as object);
                throw err;
            }
        },
        [selected]
    );

    const addServiceToTravelFile = useCallback<ContextTravelFile['addServiceToTravelFile']>(
        async (body) => {
            try {
                const response = await api.addServiceToTravelFile(body);
                // PG returns { service }, old MongoDB returned { service, updatedTravelFile }
                const newService = response?.service || response;
                const newTravelFile = cloneDeep(selected as OldTravelFile);
                newTravelFile.services.push(newService);
                setSelected(newTravelFile);
                return response;
            } catch (err) {
                setError(err as object);
                throw err;
            }
        },
        [selected]
    );
    const updateRoomsGroupTravelFile = useCallback<ContextTravelFile['updateRoomsGroupTravelFile']>(
        async (body) => {
            try {
                await api.updateRoomsGroupTravelFile(body);
                const updatedTravelFile = cloneDeep(selected as OldTravelFile);

                // Build a lookup of updated room data by passengerId
                // First resolve passengerId for each updated passenger
                const roomUpdatesByPassengerId: Record<
                    string,
                    { roomId: any; roomType: any; roomIndex: any }
                > = {};
                body.passengersToUpdateRoom.forEach((passenger) => {
                    // Find the passengerId from the group TF's passenger row
                    const tfPass = (updatedTravelFile.passengers as any[])?.find(
                        (p) => String(p._id) === String(passenger.travelFilePassengerId)
                    );
                    const passengerId = tfPass?.passengerId || passenger.travelFilePassengerId;
                    roomUpdatesByPassengerId[String(passengerId)] = {
                        roomId: passenger.roomId,
                        roomType: passenger.roomType,
                        roomIndex: passenger.roomIndex
                    };
                    // Also update the group TF passenger directly
                    if (tfPass) {
                        tfPass.roomId = passenger.roomId;
                        tfPass.roomType = passenger.roomType;
                        tfPass.roomIndex = passenger.roomIndex;
                    }
                });

                // Update relatedGroupTravelFiles — both TF-level and service-level passengers
                (updatedTravelFile.relatedGroupTravelFiles || []).forEach((relatedTravelFile) => {
                    // Update child TF passengers (PG flat model)
                    (relatedTravelFile.passengers || []).forEach((childPass: any) => {
                        const update =
                            roomUpdatesByPassengerId[String(childPass.passengerId || childPass._id)];
                        if (update) {
                            childPass.roomId = update.roomId;
                            childPass.roomType = update.roomType;
                            childPass.roomIndex = update.roomIndex;
                        }
                    });
                    // Update service-level passengers if available (MongoDB structure)
                    const firstTourOrHotelServiceIndex = relatedTravelFile.services?.findIndex((service) =>
                        checkIsTourOrHotelService(service.type, service?.info?.status)
                    );
                    if (firstTourOrHotelServiceIndex == null || firstTourOrHotelServiceIndex === -1) return;
                    const servicePassengers =
                        relatedTravelFile.services[firstTourOrHotelServiceIndex]?.passengers;
                    if (!servicePassengers) return;
                    servicePassengers.forEach((srvPass) => {
                        const update =
                            roomUpdatesByPassengerId[
                                String((srvPass as any).passengerId || srvPass.travelFilePassengerId)
                            ];
                        if (update) {
                            srvPass.roomId = update.roomId;
                            srvPass.roomType = update.roomType;
                            srvPass.roomIndex = update.roomIndex;
                        }
                    });
                });

                setSelected(updatedTravelFile);
                return true;
            } catch (err) {
                setError(err as object);
                throw err;
            }
        },
        [selected]
    );

    const updateTravelFileRemarks = useCallback<ContextTravelFile['updateTravelFileRemarks']>(
        async (body) => {
            try {
                const updatedTravelFile = await api.updateTravelFileRemarks(body);
                setSelected(updatedTravelFile);
                return true;
            } catch (err) {
                setError(err as object);
                throw err;
            }
        },
        [selected]
    );

    const registerPassengersUpdateTourAndTravelFile = useCallback<
        ContextTravelFile['registerPassengersUpdateTourAndTravelFile']
    >((body) => {
        return api
            .registerPassengersUpdateTourAndTravelFile(body)
            .then((/* travelFiles: TravelFile[] */) => {
                // setData(travelFiles);
                return true;
            })
            .catch((err) => {
                throw err;
            });
    }, []);

    const createManualTravelFileByPNR = useCallback<ContextTravelFile['createManualTravelFileByPNR']>(
        async (body) => {
            try {
                const travelFile = await api.createManualTravelFileByPNR(body);
                return travelFile;
            } catch (err) {
                throw err;
            }
        },
        []
    );

    const getGroupTravelFilesByTourDetails = useCallback<
        ContextTravelFile['getGroupTravelFilesByTourDetails']
    >((query) => {
        return api
            .getGroupTravelFilesByTourDetails(query)
            .then((travelFiles: OldTravelFile[]) => {
                setData(travelFiles);
                return travelFiles;
            })
            .catch((err) => {
                throw err;
            });
    }, []);

    const getClientTravelFilesByGroupId = useCallback<ContextTravelFile['getClientTravelFilesByGroupId']>(
        (groupId) => {
            return api
                .getClientTravelFilesByGroupId(groupId)
                .then((travelFiles: OldTravelFile[]) => {
                    setData(travelFiles);
                    return travelFiles;
                })
                .catch((err) => {
                    throw err;
                });
        },
        []
    );

    const getFullService = useCallback<ContextTravelFile['getFullService']>((serviceId) => {
        return api.getFullService(serviceId);
    }, []);

    const closeTravelFile = useCallback<ContextTravelFile['closeTravelFile']>(
        async (body) => {
            const result = await api.closeTravelFile(body);
            // Refresh selected travel file if it was closed
            if (result?.passed && selected?._id === body.travelFileId) {
                const updatedTf = { ...selected, status: 'Close' } as OldTravelFile;
                setSelected(updatedTf);
            }
            return result;
        },
        [selected]
    );

    const reopenTravelFile = useCallback<ContextTravelFile['reopenTravelFile']>(
        async (body) => {
            const result = await api.reopenTravelFile(body);
            if (selected?._id === body.travelFileId) {
                const updatedTf = { ...selected, status: 'Open' } as OldTravelFile;
                setSelected(updatedTf);
            }
            return result;
        },
        [selected]
    );

    const deleteService = useCallback<ContextTravelFile['deleteService']>(
        async (travelFileServiceId) => {
            await api.deleteService({ travelFileServiceId });
            const updatedTf = cloneDeep(selected as OldTravelFile);
            updatedTf.services = updatedTf.services.filter((s) => s._id !== travelFileServiceId);
            setSelected(updatedTf);
        },
        [selected]
    );

    // Undo a soft-delete. Server clears isDeleted (and re-links the tour group).
    // The caller refetches the TF afterwards so group links + the un-struck row
    // reflect accurately — local state alone can't recover the new group links.
    const restoreService = useCallback<ContextTravelFile['restoreService']>(
        async (travelFileServiceId) => {
            await api.restoreService({ travelFileServiceId });
        },
        []
    );

    const deleteTravelFile = useCallback(
        async (body: {
            travelFileId: string | number;
            currUserDetails?: { id?: string; name?: string; email?: string };
        }) => {
            // Backend returns 409 when blocked; let the caller catch and read
            // err.response.data.reasons to surface specifics.
            return api.deleteTravelFile(body);
        },
        []
    );

    // Open / In Process → Cancelled. When the cancelled file is the one on
    // screen, reflect the new status locally so the page locks without waiting
    // for a refetch.
    const cancelTravelFile = useCallback<ContextTravelFile['cancelTravelFile']>(
        async (body) => {
            const result = await api.cancelTravelFile(body);
            // Functional update: a refetch may land while the request is in flight,
            // and spreading the pre-await `selected` would overwrite it.
            setSelected((prev) =>
                prev && String(prev._id) === String(body.travelFileId)
                    ? ({ ...prev, status: result?.status || 'Cancelled' } as OldTravelFile)
                    : prev
            );
            return result;
        },
        []
    );

    // Cancelled → Open / In Process (the server decides which). Same local
    // reflection as cancel, with the status the server restored.
    const undoCancelTravelFile = useCallback<ContextTravelFile['undoCancelTravelFile']>(
        async (body) => {
            const result = await api.undoCancelTravelFile(body);
            setSelected((prev) =>
                prev && String(prev._id) === String(body.travelFileId) && result?.status
                    ? ({ ...prev, status: result.status } as OldTravelFile)
                    : prev
            );
            return result;
        },
        []
    );

    return (
        <Context.Provider
            value={{
                get,
                getOne,
                post,
                put,
                remove,
                softDelete,
                data,
                setData,
                selected,
                setSelected,
                error,
                selectedPassengersIds,
                setSelectedPassengersIds,
                priceExcludedPassengerIdsByTf,
                setPriceExcludedPassengerIdsByTf,
                getTourServicePassengersByTravelFile,
                getTourPassengersByTourId,
                tourPassengersData,
                setTourPassengersData,
                createTravelFileByService,
                createTravelFileNoService,
                getTravelFile,
                updateTravelFileService,
                amendTravelFileService,
                addServiceToTravelFile,
                updateRoomsGroupTravelFile,
                getTableTravelFiles,
                getTravelFiles,
                getTravelFilesByIds,
                getLedgerNumbering,
                markTravelFilesLedgerExported,
                sendTravelFileToCrm,
                getTravelFilesByTicketNumber,
                getTravelFilesByTourDetails,
                getTravelFilesByInventory,
                getTravelFilesByPassengerDetails,
                updateTravelFileRemarks,
                registerPassengersUpdateTourAndTravelFile,
                query: queryFn,
                resetContext,
                createManualTravelFileByPNR,
                getGroupTravelFilesByTourDetails,
                getClientTravelFilesByGroupId,
                getFullService,
                closeTravelFile,
                reopenTravelFile,
                deleteService,
                restoreService,
                deleteTravelFile,
                cancelTravelFile,
                undoCancelTravelFile,
                // No local patch: the populated travelAgencyAccount object feeds
                // commission and invoice defaults — the caller re-reads the file.
                updateTravelAgent: api.updateTravelAgent,
                getSalesAndProfit,
                getSalesAndProfitSummary,
                getReservationsReport,
                getCancelledPassengersReport,
                getLeftToPayReport
            }}
        >
            {children}
        </Context.Provider>
    );
}

export default TravelFileProvider;
